<!-- SkyPlus Contract Details - Split Screen View -->
<!-- Layout: Document Preview (LEFT - larger) | Metadata (RIGHT - smaller) -->
<div class="sp-dashboard-container">
  <!-- HEADER -->
  <sp-header></sp-header>

  <!-- MAIN CONTENT -->
  <main class="sp-main contract-details-main">
    
    <!-- Page Header with Breadcrumb -->
    <div class="sp-page-header compact">
      <div class="breadcrumb">
        <button class="btn-back" (click)="goBack()">
          <span class="material-icons">arrow_back</span>
        </button>
        <span class="breadcrumb-item" (click)="goBack()">Contracts</span>
        <span class="material-icons breadcrumb-separator">chevron_right</span>
        <span class="breadcrumb-item current">{{ contract?.contractNumber || contract?.name || 'Loading...' }}</span>
      </div>
      
      <div class="sp-header-actions" *ngIf="contract">
        <button class="btn btn-outline" (click)="downloadDocx()">
          <span class="material-icons">download</span> DOCX
        </button>
        <button class="btn btn-outline" (click)="downloadPdf()">
          <span class="material-icons">picture_as_pdf</span> PDF
        </button>
        <button class="btn btn-outline" (click)="printContract()">
          <span class="material-icons">print</span> Print
        </button>
        <button class="btn btn-primary" (click)="editContract()">
          <span class="material-icons">edit</span> Edit
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div class="loading-container" *ngIf="isLoading">
      <div class="loading-spinner">
        <span class="material-icons spinning">autorenew</span>
        <p>Loading contract details...</p>
      </div>
    </div>

    <!-- Error State -->
    <div class="error-container" *ngIf="errorMessage && !isLoading">
      <span class="material-icons">error_outline</span>
      <h3>Failed to load contract</h3>
      <p>{{ errorMessage }}</p>
      <button class="btn btn-primary" (click)="retry()">
        <span class="material-icons">refresh</span> Retry
      </button>
    </div>

    <!-- ── TAB BAR ───────────────────────────────────────────────────── -->
    <div class="tab-bar" *ngIf="!isLoading && !errorMessage && contract">
      <button class="tab-btn" [class.active]="activeTab === 'document'" (click)="switchTab('document')">
        <span class="material-icons">description</span> Document
      </button>
      <button class="tab-btn" [class.active]="activeTab === 'invoices'" (click)="switchTab('invoices')">
        <span class="material-icons">receipt_long</span> Invoices
        <span class="tab-count" *ngIf="invoiceSummary?.invoiceCount">{{ invoiceSummary.invoiceCount }}</span>
      </button>
    </div>

    <!-- Split Screen Content -->
    <div class="split-screen-container" *ngIf="!isLoading && !errorMessage && contract && activeTab === 'document'">
      
      <!-- LEFT PANEL: Document Preview (LARGER) -->
      <div class="preview-panel">
        <div class="panel-header">
          <h3>
            <span class="material-icons">description</span>
            Document Preview
          </h3>
          <div class="preview-controls">
            <button class="btn-icon" (click)="zoomOut()" title="Zoom Out">
              <span class="material-icons">zoom_out</span>
            </button>
            <span class="zoom-level">{{ (zoomLevel * 100) | number:'1.0-0' }}%</span>
            <button class="btn-icon" (click)="zoomIn()" title="Zoom In">
              <span class="material-icons">zoom_in</span>
            </button>
            <button class="btn-icon" (click)="resetZoom()" title="Fit to Width">
              <span class="material-icons">fit_screen</span>
            </button>
            <div class="divider"></div>
            <button class="btn-icon" (click)="openInNewTab()" title="Open in New Tab">
              <span class="material-icons">open_in_new</span>
            </button>
          </div>
        </div>

        <div class="document-container">
          <!-- Document Loading -->
          <div class="document-loading" *ngIf="isPreviewLoading">
            <span class="material-icons spinning">autorenew</span>
            <p>Rendering document...</p>
          </div>

          <!-- PDF / Image iframe viewer -->
          <iframe *ngIf="pdfBlobUrl && !isPreviewLoading"
                  [src]="pdfBlobUrl"
                  class="pdf-iframe"
                  [style.transform]="'scale(' + zoomLevel + ')'"
                  [style.transform-origin]="'top left'"
                  frameborder="0">
          </iframe>

          <!-- Mammoth.js DOCX Viewer -->
          <div class="document-scroll-wrapper"
               *ngIf="mammothHtml && !isPreviewLoading">
            <div class="docx-page-wrap"
                 [style.zoom]="zoomLevel">
              <div class="docx-viewer" [innerHTML]="mammothHtml"></div>
            </div>
          </div>

          <!-- Mammoth warnings (dev info, subtle) -->
          <div class="mammoth-warnings" *ngIf="mammothMessages.length > 0 && mammothHtml">
            <span class="material-icons">info_outline</span>
            {{ mammothMessages.length }} style(s) not mapped
          </div>

          <!-- No Preview / Error — shown only when neither PDF nor DOCX rendered -->
          <div class="no-preview" *ngIf="!mammothHtml && !pdfBlobUrl && !isPreviewLoading">
            <span class="material-icons">insert_drive_file</span>
            <h3>Preview not available</h3>
            <p>{{ previewError || 'This document could not be rendered.' }}</p>
            <p class="hint">Download the document to view its contents.</p>
            <div class="no-preview-actions">
              <button class="btn btn-primary" (click)="downloadDocx()">
                <span class="material-icons">download</span> Download DOCX
              </button>
              <button class="btn btn-outline" (click)="loadPreview()">
                <span class="material-icons">refresh</span> Retry
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT PANEL: Metadata (SMALLER) -->
      <div class="metadata-panel" [class.collapsed]="metadataCollapsed">
        <div class="panel-header">
          <h3>
            <span class="material-icons">info</span>
            <span class="panel-title-text">Contract Details</span>
          </h3>
          <button class="btn-icon collapse-btn" (click)="metadataCollapsed = !metadataCollapsed">
            <span class="material-icons">{{ metadataCollapsed ? 'chevron_left' : 'chevron_right' }}</span>
          </button>
        </div>

        <ng-container *ngIf="!metadataCollapsed">

          <!-- ===== ACTIONS BAR ===== -->
          <div class="actions-bar">
            <div class="actions-bar-label">Actions</div>
            <div class="actions-row">
              <!-- Open in OnlyOffice — only for DOCX/Word files -->
              <button class="action-btn action-btn-onlyoffice" (click)="openInOnlyOffice()" title="Edit in OnlyOffice"
                      *ngIf="isDocxFile">
                <span class="material-icons">edit_document</span> OnlyOffice
              </button>
              <!-- Start Review Workflow — only visible when contract is Draft -->
              <button class="action-btn action-btn-workflow" (click)="showWorkflowConfirm = true" title="Start review workflow"
                      *ngIf="isDraftContract">
                <span class="material-icons">rate_review</span> Start Review
              </button>
            </div>
            <div class="actions-row">
              <!-- Download DOCX — only shown for Word documents -->
              <button class="action-btn" (click)="downloadDocx()" title="Download DOCX" *ngIf="isDocxFile">
                <span class="material-icons">description</span> Download DOCX
              </button>
              <!-- Download PDF — only shown for PDF files -->
              <button class="action-btn" (click)="downloadPdf()" title="Download PDF" *ngIf="isPdfFile">
                <span class="material-icons">picture_as_pdf</span> Download PDF
              </button>
              <!-- Edit Properties -->
              <button class="action-btn action-btn-primary" (click)="startEditProperties()" title="Edit Alfresco custom properties">
                <span class="material-icons">tune</span> Edit Properties
              </button>
            </div>
          </div>

          <!-- ===== EDIT PROPERTIES OVERLAY ===== -->
          <div class="edit-properties-overlay" *ngIf="isEditingProperties" style="position:relative;">
            <div class="edit-properties-header">
              <h4><span class="material-icons">tune</span> Edit Properties</h4>
              <div class="edit-header-actions">
                <button class="btn btn-outline" (click)="cancelEditProperties()" [disabled]="isSavingProperties">
                  Cancel
                </button>
                <button class="btn btn-primary" (click)="saveProperties()" [disabled]="isSavingProperties">
                  <span class="material-icons spinning" *ngIf="isSavingProperties">autorenew</span>
                  <span class="material-icons" *ngIf="!isSavingProperties">save</span>
                  {{ isSavingProperties ? 'Saving…' : 'Save' }}
                </button>
              </div>
            </div>

            <div class="edit-save-error" *ngIf="saveError">
              <span class="material-icons">error_outline</span> {{ saveError }}
            </div>

            <div class="edit-properties-form">

              <!-- Core Info -->
              <div class="edit-section">
                <div class="edit-section-title">Contract Information</div>
                <div class="edit-field-grid">
                  <div class="edit-field edit-field-full">
                    <label>Contract Title</label>
                    <input type="text" [(ngModel)]="editableContract.contractTitle" placeholder="Contract title">
                  </div>
                  <div class="edit-field">
                    <label>Contract Type</label>
                    <select [(ngModel)]="editableContract.contractType">
                      <option value="">— Select —</option>
                      <option *ngFor="let t of contractTypes" [value]="t">{{ t }}</option>
                    </select>
                  </div>
                  <div class="edit-field">
                    <label>Status</label>
                    <select [(ngModel)]="editableContract.contractStatus">
                      <option *ngFor="let s of contractStatuses" [value]="s">{{ s }}</option>
                    </select>
                  </div>
                  <div class="edit-field">
                    <label>Priority</label>
                    <select [(ngModel)]="editableContract.priority">
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Parties -->
              <div class="edit-section">
                <div class="edit-section-title">Parties</div>
                <div class="edit-field-grid">
                  <div class="edit-field edit-field-full">
                    <label>Supplier / Vendor</label>
                    <input type="text" [(ngModel)]="editableContract.supplierName" placeholder="Supplier name">
                  </div>
                  <div class="edit-field edit-field-full">
                    <label>Department</label>
                    <input type="text" [(ngModel)]="editableContract.departmentName" placeholder="Department name">
                  </div>
                </div>
              </div>

              <!-- Financial -->
              <div class="edit-section">
                <div class="edit-section-title">Financial</div>
                <div class="edit-field-grid">
                  <div class="edit-field">
                    <label>Contract Amount</label>
                    <input type="number" [(ngModel)]="editableContract.contractAmount" placeholder="0.00" min="0">
                  </div>
                  <div class="edit-field">
                    <label>Currency</label>
                    <select [(ngModel)]="editableContract.currency">
                      <option value="KES">KES</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Dates -->
              <div class="edit-section">
                <div class="edit-section-title">Key Dates</div>
                <div class="edit-field-grid">
                  <div class="edit-field">
                    <label>Effective Date</label>
                    <input type="date" [(ngModel)]="editableContract.effectiveDate">
                  </div>
                  <div class="edit-field">
                    <label>Expiry Date</label>
                    <input type="date" [(ngModel)]="editableContract.expiryDate">
                  </div>
                </div>
              </div>

              <!-- Notes -->
              <div class="edit-section">
                <div class="edit-section-title">Notes</div>
                <div class="edit-field-grid">
                  <div class="edit-field edit-field-full">
                    <label>Internal Notes</label>
                    <textarea  rows="3" placeholder="Internal notes (not visible to vendor)"></textarea>
                  </div>
                </div>
              </div>

            </div><!-- /edit-properties-form -->
          </div><!-- /edit-properties-overlay -->

          <!-- ===== WORKFLOW CONFIRM DIALOG ===== -->
            <!-- ── SUCCESS TOAST ───────────────────────────────────────── -->
          <div class="workflow-toast workflow-toast--success" *ngIf="workflowSuccessMessage">
            <span class="material-icons">check_circle</span>
            <span>{{ workflowSuccessMessage }}</span>
          </div>

          <!-- ── ERROR BANNER (persistent until dismissed) ─────────── -->
          <div class="workflow-toast workflow-toast--error" *ngIf="workflowErrorMessage">
            <span class="material-icons">error_outline</span>
            <span>{{ workflowErrorMessage }}</span>
            <button class="workflow-toast__dismiss" (click)="dismissWorkflowError()" title="Dismiss">
              <span class="material-icons">close</span>
            </button>
          </div>

          <!-- ── CONFIRM DIALOG ──────────────────────────────────────── -->
          <div class="workflow-confirm-overlay" *ngIf="showWorkflowConfirm">
            <div class="workflow-confirm-card">
              <h4><span class="material-icons">rate_review</span> Start Review Workflow</h4>
              <p>
                This will change the contract status to <strong>Pending Review</strong>
                and notify the assigned reviewers. Continue?
              </p>
              <div class="workflow-confirm-actions">
                <button class="btn btn-outline"
                        [disabled]="isStartingWorkflow"
                        (click)="showWorkflowConfirm = false">Cancel</button>
                <button class="btn action-btn-workflow btn-primary"
                        [disabled]="isStartingWorkflow"
                        (click)="startReviewWorkflow()">
                  <span class="material-icons" *ngIf="!isStartingWorkflow">send</span>
                  <span class="material-icons spin-icon" *ngIf="isStartingWorkflow">refresh</span>
                  {{ isStartingWorkflow ? 'Starting…' : 'Start' }}
                </button>
              </div>
            </div>
          </div>

          <!-- ===== READ VIEW ===== -->
          <div class="panel-content" *ngIf="!isEditingProperties">

            <!-- Contract Title & Status Card -->
            <div class="contract-header-card">
              <h2 class="contract-title">{{ contract.contractTitle || contract.name }}</h2>
              <div class="contract-badges">
                <span class="status-badge" [ngClass]="getStatusClass(contract.contractStatus)">
                  {{ contract.contractStatus || 'Draft' }}
                </span>
                <span class="priority-badge" [ngClass]="getPriorityClass(contract.priority)" *ngIf="contract.priority">
                  {{ contract.priority }}
                </span>
              </div>
            </div>

            <!-- Contract Information -->
            <div class="info-section">
              <h4 class="section-title">
                <span class="material-icons">assignment</span>
                Contract Information
              </h4>
              <div class="info-grid">
                <div class="info-item">
                  <label>Contract Number</label>
                  <span class="value highlight">{{ contract.contractNumber || '-' }}</span>
                </div>
                <div class="info-item">
                  <label>Contract Type</label>
                  <span class="value">{{ contract.contractType || '-' }}</span>
                </div>
              </div>
            </div>

            <!-- Parties -->
            <div class="info-section">
              <h4 class="section-title">
                <span class="material-icons">business</span>
                Parties
              </h4>
              <div class="info-grid">
                <div class="info-item full-width">
                  <label>Supplier / Vendor</label>
                  <span class="value">{{ contract.supplierName || '-' }}</span>
                </div>
                <div class="info-item full-width">
                  <label>Department</label>
                  <span class="value">{{ contract.departmentName || '-' }}</span>
                </div>
              </div>
            </div>

            <!-- Financial -->
            <div class="info-section">
              <h4 class="section-title">
                <span class="material-icons">payments</span>
                Financial
              </h4>
              <div class="info-grid">
                <div class="info-item full-width">
                  <label>Contract Value</label>
                  <span class="value amount" *ngIf="contract.contractAmount">
                    {{ formatCurrency(contract.contractAmount, contract.currency) }}
                  </span>
                  <span class="value" *ngIf="!contract.contractAmount">-</span>
                </div>
              </div>
            </div>

            <!-- Key Dates -->
            <div class="info-section">
              <h4 class="section-title">
                <span class="material-icons">event</span>
                Key Dates
              </h4>
              <div class="info-grid">
                <div class="info-item">
                  <label>Effective Date</label>
                  <span class="value">{{ formatDate(contract.effectiveDate) }}</span>
                </div>
                <div class="info-item">
                  <label>Expiry Date</label>
                  <span class="value" [class.expiring-soon]="isExpiringSoon()" [class.expired]="isExpired()">
                    {{ formatDate(contract.expiryDate) }}
                  </span>
                </div>
                <div class="info-item full-width" *ngIf="contract.expiryDate">
                  <label>Status</label>
                  <span class="value days-badge" [ngClass]="getDaysRemainingClass()">
                    <ng-container *ngIf="calculateDaysRemaining() < 0">
                      Expired {{ -calculateDaysRemaining() }} days ago
                    </ng-container>
                    <ng-container *ngIf="calculateDaysRemaining() >= 0">
                      {{ calculateDaysRemaining() }} days remaining
                    </ng-container>
                  </span>
                </div>
              </div>
            </div>

            <!-- Document Info -->
            <div class="info-section">
              <h4 class="section-title">
                <span class="material-icons">insert_drive_file</span>
                Document Info
              </h4>
              <div class="info-grid">
                <div class="info-item full-width">
                  <label>File Name</label>
                  <span class="value small">{{ contract.name }}</span>
                </div>
                <div class="info-item">
                  <label>Size</label>
                  <span class="value">{{ formatFileSize(contract.sizeInBytes) }}</span>
                </div>
                <div class="info-item">
                  <label>Created By</label>
                  <span class="value">{{ contract.createdBy || '-' }}</span>
                </div>
                <div class="info-item">
                  <label>Created</label>
                  <span class="value">{{ formatDateTime(contract.createdAt) }}</span>
                </div>
                <div class="info-item">
                  <label>Modified By</label>
                  <span class="value">{{ contract.modifiedBy || '-' }}</span>
                </div>
                <div class="info-item">
                  <label>Last Modified</label>
                  <span class="value">{{ formatDateTime(contract.modifiedAt) }}</span>
                </div>
              </div>
            </div>

            <!-- Additional Metadata -->
            <div class="info-section" *ngIf="contract.metadata && (contract.metadata | keyvalue)?.length > 0">
              <h4 class="section-title">
                <span class="material-icons">label</span>
                Additional Properties
              </h4>
              <div class="info-grid">
                <ng-container *ngFor="let prop of contract.metadata | keyvalue">
                  <div class="info-item" *ngIf="prop.value">
                    <label>{{ formatPropertyLabel(prop.key) }}</label>
                    <span class="value">{{ prop.value }}</span>
                  </div>
                </ng-container>
              </div>
            </div>

          </div><!-- /panel-content read view -->

        </ng-container>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════════════
         INVOICES TAB
    ═══════════════════════════════════════════════════════════════════ -->
    <div class="invoices-tab-panel" *ngIf="!isLoading && !errorMessage && contract && activeTab === 'invoices'">

      <!-- Loading -->
      <div class="inv-loading" *ngIf="isLoadingInvoices">
        <span class="material-icons spinning">autorenew</span> Loading invoices…
      </div>

      <!-- Error -->
      <div class="inv-error" *ngIf="invoiceError && !isLoadingInvoices">
        <span class="material-icons">error_outline</span> {{ invoiceError }}
      </div>

      <ng-container *ngIf="!isLoadingInvoices">

        <!-- ── CONTRACT AMOUNT WARNING ──────────────────────────────── -->
        <div class="inv-amount-warn" *ngIf="invoiceSummary?.contractAmountMissing">
          <span class="material-icons">info</span>
          Contract value is not set. <a (click)="activeTab = 'document'">Edit the contract</a> and add a Contract Amount to enable balance tracking.
        </div>

        <!-- ── FINANCIALS STRIP ─────────────────────────────────────── -->
        <div class="financials-strip" *ngIf="invoiceSummary && !invoiceSummary.contractAmountMissing">
          <div class="fin-card">
            <div class="fin-label">Contract Value</div>
            <div class="fin-value primary">{{ formatCurrency(invoiceSummary.contractAmount, invoiceSummary.currency || contract?.currency || 'KES') }}</div>
          </div>
          <div class="fin-card">
            <div class="fin-label">Total Invoiced</div>
            <div class="fin-value">{{ formatCurrency(invoiceSummary.totalInvoiced, invoiceSummary.currency || contract?.currency || 'KES') }}</div>
            <div class="fin-sub">{{ invoiceSummary.invoiceCount }} invoice{{ invoiceSummary.invoiceCount !== 1 ? 's' : '' }}</div>
          </div>
          <div class="fin-card">
            <div class="fin-label">Total Paid</div>
            <div class="fin-value paid">{{ formatCurrency(invoiceSummary.totalPaid, invoiceSummary.currency || contract?.currency || 'KES') }}</div>
          </div>
          <div class="fin-card" [ngClass]="getBalanceClass()">
            <div class="fin-label">Remaining Balance</div>
            <div class="fin-value balance">{{ formatCurrency(invoiceSummary.remainingBalance, invoiceSummary.currency || contract?.currency || 'KES') }}</div>
            <div class="fin-sub">{{ invoiceSummary.percentageInvoiced | number:'1.0-1' }}% invoiced</div>
          </div>
          <!-- Progress bar -->
          <div class="fin-progress-wrap">
            <div class="fin-progress-bar">
              <div class="fin-progress-fill" [ngClass]="getBalanceClass()"
                   [style.width.%]="invoiceSummary.percentageInvoiced"></div>
            </div>
            <span class="fin-progress-label">{{ invoiceSummary.percentageInvoiced | number:'1.0-1' }}% of contract invoiced</span>
          </div>
        </div>

        <!-- ── INVOICES PANEL HEADER ─────────────────────────────────── -->
        <div class="inv-panel-header">
          <h3><span class="material-icons">receipt_long</span> Invoices
            <span class="inv-count" *ngIf="invoices.length">({{ invoices.length }})</span>
          </h3>
          <button class="btn btn-primary btn-sm" (click)="openAddInvoice()">
            <span class="material-icons">add</span> Upload Invoice
          </button>
        </div>

        <!-- ── EMPTY STATE ───────────────────────────────────────────── -->
        <div class="inv-empty" *ngIf="!invoices.length && !showAddInvoice">
          <span class="material-icons inv-empty-icon">receipt_long</span>
          <p class="inv-empty-title">No invoices linked to this contract</p>
          <p class="inv-empty-sub">Click <strong>Upload Invoice</strong> to start tracking invoices against this contract.</p>
          <button class="btn btn-primary" (click)="openAddInvoice()">
            <span class="material-icons">add</span> Upload Invoice
          </button>
        </div>

        <!-- ── ADD / EDIT INVOICE FORM ───────────────────────────────── -->
        <div class="inv-form-card" *ngIf="showAddInvoice">
          <div class="inv-form-header">
            <h4>{{ editingInvoiceId ? 'Edit Invoice' : 'New Invoice' }}</h4>
            <button class="btn-icon-xs" (click)="closeInvoiceDialog()"><span class="material-icons">close</span></button>
          </div>
          <div class="inv-form-error" *ngIf="invoiceFormError">
            <span class="material-icons">error_outline</span> {{ invoiceFormError }}
          </div>
          <div class="inv-form-grid">
            <div class="inv-form-group">
              <label>Invoice Number <span class="req">*</span></label>
              <input type="text" [(ngModel)]="invoiceForm.invoiceNumber" class="form-control" placeholder="e.g. INV-2025-001">
            </div>
            <div class="inv-form-group">
              <label>Invoice Date <span class="req">*</span></label>
              <input type="date" [(ngModel)]="invoiceForm.invoiceDate" class="form-control">
            </div>
            <div class="inv-form-group">
              <label>Amount <span class="req">*</span></label>
              <input type="number" [(ngModel)]="invoiceForm.invoiceAmount" class="form-control" min="0" placeholder="0.00">
            </div>
            <div class="inv-form-group">
              <label>Currency</label>
              <select [(ngModel)]="invoiceForm.currency" class="form-control">
                <option *ngFor="let c of invoiceCurrencies" [value]="c">{{ c }}</option>
              </select>
            </div>
            <div class="inv-form-group">
              <label>Status</label>
              <select [(ngModel)]="invoiceForm.invoiceStatus" class="form-control">
                <option *ngFor="let s of invoiceStatuses" [value]="s">{{ s }}</option>
              </select>
            </div>
            <div class="inv-form-group">
              <label>PO Reference</label>
              <input type="text" [(ngModel)]="invoiceForm.poReference" class="form-control" placeholder="e.g. PO-2025-042">
            </div>
            <div class="inv-form-group full">
              <label>Description</label>
              <textarea [(ngModel)]="invoiceForm.description" class="form-control" rows="2" placeholder="Brief description of goods/services invoiced"></textarea>
            </div>
            <div class="inv-form-group full">
              <label>Invoice Document <span class="hint">(optional)</span></label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" (change)="onInvoiceFileSelected($event)" class="form-control-file">
              <span class="file-hint" *ngIf="pendingInvoiceFile">{{ pendingInvoiceFile.name }}</span>
            </div>
          </div>
          <div class="inv-form-actions">
            <button class="btn btn-outline btn-sm" (click)="closeInvoiceDialog()">Cancel</button>
            <button class="btn btn-primary btn-sm" [disabled]="isSavingInvoice" (click)="saveInvoice()">
              <span class="material-icons spinning" *ngIf="isSavingInvoice">autorenew</span>
              <span class="material-icons" *ngIf="!isSavingInvoice">save</span>
              {{ isSavingInvoice ? 'Saving…' : (editingInvoiceId ? 'Update Invoice' : 'Save Invoice') }}
            </button>
          </div>
        </div>

        <!-- ── INVOICE TABLE ─────────────────────────────────────────── -->
        <div class="inv-table-wrap" *ngIf="invoices.length">
          <table class="inv-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Date</th>
                <th>Description</th>
                <th class="text-right">Amount</th>
                <th>Status</th>
                <th>Document</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let inv of invoices">
                <td class="inv-num">{{ inv.invoiceNumber }}</td>
                <td class="inv-date">{{ formatDate(inv.invoiceDate) }}</td>
                <td class="inv-desc">{{ inv.description || '—' }}</td>
                <td class="inv-amount text-right">{{ formatCurrency(inv.invoiceAmount, inv.currency) }}</td>
                <td>
                  <select class="status-select" [value]="inv.invoiceStatus"
                          (change)="changeInvoiceStatus(inv, $event.target['value'])">
                    <option *ngFor="let s of invoiceStatuses" [value]="s">{{ s }}</option>
                  </select>
                </td>
                <td class="inv-doc">
                  <button *ngIf="inv.hasDocument" class="btn-icon-xs" (click)="downloadInvoiceDoc(inv)" title="Download document">
                    <span class="material-icons">download</span>
                  </button>
                  <span *ngIf="!inv.hasDocument" class="no-doc">No file</span>
                </td>
                <td class="inv-actions">
                  <button class="btn-icon-xs" (click)="openEditInvoice(inv)" title="Edit">
                    <span class="material-icons">edit</span>
                  </button>
                  <button class="btn-icon-xs danger" (click)="deleteInvoice(inv)" title="Delete">
                    <span class="material-icons">delete_outline</span>
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot *ngIf="invoiceSummary">
              <tr class="totals-row">
                <td colspan="3"><strong>Total Invoiced</strong></td>
                <td class="text-right"><strong>{{ formatCurrency(invoiceSummary.totalInvoiced, contract?.currency || 'KES') }}</strong></td>
                <td colspan="3"></td>
              </tr>
            </tfoot>
          </table>
        </div>

      </ng-container>
    </div><!-- /invoices-tab-panel -->

  </main>
</div>